import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Zap, Send, CheckCircle2, XCircle, BookCheck,
  AlertTriangle, Info, RotateCcw, Link, Unlink, Loader2, Link2,
} from 'lucide-react';

import { cardReconciliationApi } from '@/api/cardReconciliation.api';
import { usePermission }          from '@/hooks/usePermission';
import { Permissions }            from '@/constants/permissions';
import { Spinner }                from '@/components/ui/Spinner';
import { cn }                     from '@/utils/cn';

import type {
  CardReconciliationDto,
  CardReconciliationLineDto,
  AutoMatchResult,
} from '@/types/cardReconciliation.types';

// ── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ name }: { name: string }) => {
  const color = {
    Draft:       'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    'In Progress': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    Submitted:   'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
    Approved:    'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    Rejected:    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    Posted:      'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    Reversed:    'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  }[name] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';

  return (
    <span className={cn('px-2.5 py-1 rounded-full text-xs font-semibold', color)}>{name}</span>
  );
};

// ── Match Status badge ────────────────────────────────────────────────────────

const MatchBadge = ({ name }: { name: string }) => {
  const color = {
    Unmatched:        'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    Matched:          'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    PartiallyMatched: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    Variance:         'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
    Posted:           'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  }[name] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';

  return <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', color)}>{name}</span>;
};

// ��─ Amount cell ───────────────────────────────────────────────────────────────

const Amt = ({ v, highlight = false }: { v: number; highlight?: boolean }) => (
  <span className={cn('font-mono tabular-nums', highlight && Math.abs(v) > 0.01 ? 'text-red-600 dark:text-red-400 font-semibold' : '')}>
    {v.toLocaleString('en-US', { minimumFractionDigits: 2 })}
  </span>
);

// ── Main workspace ────────────────────────────────────────────────────────────

export const CardReconciliationWorkspacePage = () => {
  const { id }        = useParams<{ id: string }>();
  const navigate      = useNavigate();
  const { hasPermission } = usePermission();

  const canMatch   = hasPermission(Permissions.CARD_RECON_MATCH);
  const canSubmit  = hasPermission(Permissions.CARD_RECON_SUBMIT);
  const canApprove = hasPermission(Permissions.CARD_RECON_APPROVE);
  const canPost    = hasPermission(Permissions.CARD_RECON_POST);
  const canReverse = hasPermission(Permissions.CARD_RECON_REVERSE);

  const [recon,   setRecon]   = useState<CardReconciliationDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // Auto-match dialog state
  const [showAutoMatch,  setShowAutoMatch]  = useState(false);
  const [amtTolerance,   setAmtTolerance]   = useState('0.50');
  const [dateTolerance,  setDateTolerance]  = useState('3');
  const [autoMatchResult, setAutoMatchResult] = useState<AutoMatchResult | null>(null);

  // Manual match: one unmatched sale and one unmatched statement line, picked from the list.
  const [pickedSale,       setPickedSale]       = useState<CardReconciliationLineDto | null>(null);
  const [pickedSettlement, setPickedSettlement] = useState<CardReconciliationLineDto | null>(null);
  const [matchReason,      setMatchReason]      = useState('');
  const [matchError,       setMatchError]       = useState<string | null>(null);

  // Reject dialog state
  const [showReject,  setShowReject]  = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await cardReconciliationApi.getById(id);
      if (res.data?.data) setRecon(res.data.data);
      else setError('Reconciliation not found.');
    } catch (err) {
      console.error(err);
      setError('Failed to load reconciliation.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ── Action handlers ───────────────────────────────────────────────────────

  const handleAutoMatch = async () => {
    if (!id) return;
    setActionBusy(true);
    try {
      const res = await cardReconciliationApi.autoMatch(id, {
        amountTolerance:   parseFloat(amtTolerance) || 0.50,
        dateToleranceDays: parseInt(dateTolerance) || 3,
      });
      const result = res.data?.data;
      if (result) setAutoMatchResult(result);
      await load();
      setShowAutoMatch(false);
    } catch (err) {
      console.error('Auto-match failed', err);
    } finally {
      setActionBusy(false);
    }
  };

  const handleManualMatch = async () => {
    if (!id || !pickedSale?.cardClearingItemId || !pickedSettlement?.cardSettlementLineId) return;
    setActionBusy(true);
    setMatchError(null);
    try {
      await cardReconciliationApi.manualMatch(id, {
        cardClearingItemId:   pickedSale.cardClearingItemId,
        cardSettlementLineId: pickedSettlement.cardSettlementLineId,
        varianceReason:       matchReason.trim() || null,
      });
      setPickedSale(null);
      setPickedSettlement(null);
      setMatchReason('');
      await load();
    } catch (err) {
      setMatchError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'The two lines could not be matched.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleClearMatch = async (line: CardReconciliationLineDto) => {
    if (!id || !canMatch) return;
    setActionBusy(true);
    try {
      await cardReconciliationApi.clearMatch(id, line.id);
      await load();
    } catch (err) {
      console.error('Clear match failed', err);
    } finally {
      setActionBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!id) return;
    setActionBusy(true);
    try {
      await cardReconciliationApi.submit(id);
      await load();
    } catch (err) {
      console.error('Submit failed', err);
    } finally {
      setActionBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    setActionBusy(true);
    try {
      await cardReconciliationApi.approve(id);
      await load();
    } catch (err) {
      console.error('Approve failed', err);
    } finally {
      setActionBusy(false);
    }
  };

  const handleReject = async () => {
    if (!id || !rejectReason.trim()) return;
    setActionBusy(true);
    try {
      await cardReconciliationApi.reject(id, rejectReason);
      setShowReject(false);
      setRejectReason('');
      await load();
    } catch (err) {
      console.error('Reject failed', err);
    } finally {
      setActionBusy(false);
    }
  };

  const handlePost = async () => {
    if (!id) return;
    setActionBusy(true);
    try {
      await cardReconciliationApi.post(id);
      await load();
    } catch (err) {
      console.error('Post failed', err);
    } finally {
      setActionBusy(false);
    }
  };

  const handleReverse = async () => {
    if (!id) return;
    setActionBusy(true);
    try {
      await cardReconciliationApi.reverse(id);
      await load();
    } catch (err) {
      console.error('Reverse failed', err);
    } finally {
      setActionBusy(false);
    }
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  // ── Loading / Error states ────────────────────────────────────────────────

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  if (error || !recon) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-500 dark:text-gray-400">
        <AlertTriangle size={40} className="mb-3 text-amber-400" />
        <p>{error ?? 'Reconciliation not found.'}</p>
        <button onClick={() => navigate(-1)} className="mt-3 text-teal-600 dark:text-teal-400 hover:underline text-sm">Go back</button>
      </div>
    );
  }

  // Still being worked on: the server lets Draft, Imported and Matched go forward to Submit.
  const isDraft      = ['Draft', 'In Progress', 'Imported', 'Matched'].includes(recon.statusName);
  const isSubmitted  = recon.statusName === 'Submitted';
  const isApproved   = recon.statusName === 'Approved';
  const isPosted     = recon.statusName === 'Posted';
  const hasDiff      = Math.abs(recon.differenceAmount) > 0.01;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate(-1)} className="mt-0.5 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors">
              <ArrowLeft size={16} />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white font-mono">
                  {recon.reconciliationNo}
                </h1>
                <StatusBadge name={recon.statusName} />
                {hasDiff && (
                  <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full ring-1 ring-amber-200">
                    <AlertTriangle size={11} />
                    Variance: {Math.abs(recon.differenceAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
                <span>{recon.acquirerName}</span>
                {recon.cardTypeName && <span>· {recon.cardTypeName}</span>}
                <span>· {fmtDate(recon.dateFrom)} – {fmtDate(recon.dateTo)}</span>
                <span>· Period {recon.financialPeriodCode}</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={load}
              disabled={actionBusy}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>

            {isDraft && canMatch && (
              <button
                onClick={() => setShowAutoMatch(true)}
                disabled={actionBusy}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
              >
                <Zap size={14} />
                Auto-Match
              </button>
            )}

            {isDraft && canSubmit && (
              <button
                onClick={handleSubmit}
                disabled={actionBusy}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50"
              >
                <Send size={14} />
                Submit
              </button>
            )}

            {isSubmitted && canApprove && (
              <>
                <button
                  onClick={handleApprove}
                  disabled={actionBusy}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors disabled:opacity-50"
                >
                  <CheckCircle2 size={14} />
                  Approve
                </button>
                <button
                  onClick={() => setShowReject(true)}
                  disabled={actionBusy}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                >
                  <XCircle size={14} />
                  Reject
                </button>
              </>
            )}

            {isApproved && canPost && (
              <button
                onClick={handlePost}
                disabled={actionBusy}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50"
              >
                <BookCheck size={14} />
                Post to GL
              </button>
            )}

            {isPosted && canReverse && (
              <button
                onClick={handleReverse}
                disabled={actionBusy}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-gray-600 text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                <RotateCcw size={14} />
                Reverse
              </button>
            )}
          </div>
        </div>

        {/* Auto-match result flash */}
        {autoMatchResult && (
          <div className="mt-3 flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
            <Info size={16} />
            <span>
              Auto-match complete: <strong>{autoMatchResult.exactMatches + autoMatchResult.fuzzyMatches}</strong> matched
              {autoMatchResult.fuzzyMatches > 0 && <> ({autoMatchResult.fuzzyMatches} within tolerance)</>},{' '}
              <strong>{autoMatchResult.unmatched}</strong> unmatched.
            </span>
            <button onClick={() => setAutoMatchResult(null)} className="ml-auto text-blue-400 hover:text-blue-600">✕</button>
          </div>
        )}
      </div>

      {/* ── Summary tiles ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3 px-6 py-4">
        {[
          { label: 'Expected Gross',   value: recon.expectedGrossAmount,  color: 'text-slate-700 dark:text-gray-300' },
          { label: 'Settled Gross',    value: recon.settledGrossAmount,   color: 'text-slate-700 dark:text-gray-300' },
          { label: 'Bank Settled',     value: recon.bankSettledAmount,    color: 'text-slate-700 dark:text-gray-300' },
          { label: 'Difference',       value: recon.differenceAmount,     color: hasDiff ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400' },
        ].map(tile => (
          <div key={tile.label} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{tile.label}</p>
            <p className={cn('text-lg font-bold font-mono tabular-nums', tile.color)}>
              {Math.abs(tile.value).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              {tile.label === 'Difference' && hasDiff && <span className="text-xs font-normal ml-1">±</span>}
            </p>
          </div>
        ))}
      </div>

      {/* ── Reconciliation Lines ──────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Reconciliation Lines ({recon.lines.length})
            </h2>
          </div>

          {canMatch && isDraft && (pickedSale || pickedSettlement) && (
            <div className="px-4 py-3 border-b border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 flex items-center gap-3 flex-wrap text-xs">
              <span className="text-slate-700 dark:text-slate-300">
                Sale: <b className="font-mono">{pickedSale ? `${pickedSale.clearingBatchNumber ?? '-'} (${pickedSale.clearingGrossAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })})` : 'pick one below'}</b>
              </span>
              <span className="text-slate-700 dark:text-slate-300">
                Statement line: <b className="font-mono">{pickedSettlement ? `${pickedSettlement.settlementBatchNumber ?? '-'} (${pickedSettlement.settlementGrossAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })})` : 'pick one below'}</b>
              </span>
              <input value={matchReason} onChange={e => setMatchReason(e.target.value)} placeholder="Reason for any difference (optional)"
                className="flex-1 min-w-[12rem] px-2 py-1 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
              <button onClick={() => void handleManualMatch()} disabled={actionBusy || !pickedSale || !pickedSettlement}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-medium disabled:opacity-40">
                <Link2 size={12} /> Match these two
              </button>
              <button onClick={() => { setPickedSale(null); setPickedSettlement(null); setMatchError(null); }}
                className="px-2 py-1.5 text-slate-500 hover:text-slate-700 dark:text-slate-400">Clear</button>
              {matchError && <span className="w-full text-red-600 dark:text-red-400">{matchError}</span>}
            </div>
          )}

          {recon.lines.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Link size={32} className="mb-3 opacity-40" />
              <p className="text-sm">No lines yet — run Auto-Match to populate</p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">#</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Match</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bill / batch</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Settlement file</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Clearing Gross</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Settlement Gross</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">MDR Exp</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">MDR Act</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Variance</th>
                  <th className="px-3 py-2 text-center font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Confidence</th>
                  <th className="px-3 py-2 text-center font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  {canMatch && isDraft && (
                    <th className="px-3 py-2 text-center font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {recon.lines.map(line => (
                  <tr key={line.id} className={cn(
                    'hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors',
                    Math.abs(line.variance) > 0.01 && 'bg-amber-50/50 dark:bg-amber-900/10',
                  )}>
                    <td className="px-3 py-2 text-slate-400">{line.lineNo}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        {line.cardClearingItemId && line.cardSettlementLineId
                          ? <Link size={11} className="text-green-500 shrink-0" />
                          : <Unlink size={11} className="text-slate-300 shrink-0" />}
                        <span className="text-[10px] text-slate-500 dark:text-gray-400">{line.matchType}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-slate-700 dark:text-slate-300">{line.clearingBatchNumber ?? '—'}</div>
                      {line.clearingSaleDate && <div className="text-slate-400 text-[10px]">{fmtDate(line.clearingSaleDate)}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-slate-700 dark:text-slate-300">{line.settlementBatchNumber ?? '—'}</div>
                      {line.settlementDate && <div className="text-slate-400 text-[10px]">{fmtDate(line.settlementDate)}</div>}
                    </td>
                    <td className="px-3 py-2 text-right"><Amt v={line.clearingGrossAmount} /></td>
                    <td className="px-3 py-2 text-right"><Amt v={line.settlementGrossAmount} /></td>
                    <td className="px-3 py-2 text-right text-slate-500 dark:text-gray-400"><Amt v={line.mdrExpected} /></td>
                    <td className="px-3 py-2 text-right text-slate-500 dark:text-gray-400"><Amt v={line.mdrActual} /></td>
                    <td className="px-3 py-2 text-right"><Amt v={line.variance} highlight /></td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-16 bg-slate-200 dark:bg-slate-600 rounded-full h-1.5">
                          <div
                            className={cn('h-1.5 rounded-full', line.matchConfidence >= 90 ? 'bg-green-500' : line.matchConfidence >= 60 ? 'bg-yellow-500' : 'bg-slate-400')}
                            style={{ width: `${Math.min(100, line.matchConfidence)}%` }}
                          />
                        </div>
                        <span className="ml-1.5 text-[10px] text-slate-500 dark:text-gray-400">{Math.round(line.matchConfidence)}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <MatchBadge name={line.matchStatusName} />
                    </td>
                    {canMatch && isDraft && (
                      <td className="px-3 py-2 text-center">
                        {line.matchStatusName === 'Unmatched' && line.cardClearingItemId && !line.cardSettlementLineId && (
                          <button
                            onClick={() => setPickedSale(pickedSale?.id === line.id ? null : line)}
                            className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors',
                              pickedSale?.id === line.id ? 'bg-teal-600 border-teal-600 text-white' : 'border-teal-300 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20')}
                            title="Pick this sale to match by hand">
                            Pick sale
                          </button>
                        )}
                        {line.matchStatusName === 'Unmatched' && line.cardSettlementLineId && !line.cardClearingItemId && (
                          <button
                            onClick={() => setPickedSettlement(pickedSettlement?.id === line.id ? null : line)}
                            className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors',
                              pickedSettlement?.id === line.id ? 'bg-teal-600 border-teal-600 text-white' : 'border-teal-300 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20')}
                            title="Pick this statement line to match by hand">
                            Pick statement line
                          </button>
                        )}
                        {line.cardClearingItemId && line.cardSettlementLineId && !line.isPosted && (
                          <button
                            onClick={() => handleClearMatch(line)}
                            disabled={actionBusy}
                            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-400 hover:text-red-600 transition-colors disabled:opacity-40"
                            title="Clear match"
                          >
                            <Unlink size={12} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Auto-match dialog ─────────────────────────────────────────────────── */}
      {showAutoMatch && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Auto-Match Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-600 dark:text-slate-400">Amount Tolerance</label>
                <input
                  type="number" step="0.01" min="0" value={amtTolerance}
                  onChange={e => setAmtTolerance(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600 dark:text-slate-400">Date Tolerance (days)</label>
                <input
                  type="number" step="1" min="0" value={dateTolerance}
                  onChange={e => setDateTolerance(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowAutoMatch(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Cancel</button>
              <button
                onClick={handleAutoMatch}
                disabled={actionBusy}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {actionBusy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                Run Auto-Match
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject dialog ─────────────────────────────────────────────────────── */}
      {showReject && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Reject Reconciliation</h3>
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400">Rejection Reason <span className="text-red-500">*</span></label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Enter reason for rejection…"
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-red-500 outline-none resize-none"
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowReject(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Cancel</button>
              <button
                onClick={handleReject}
                disabled={actionBusy || !rejectReason.trim()}
                className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {actionBusy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
